import { useState } from "react";
import { MoreHorizontal, Plus } from "lucide-react";
import { Button } from "@/components/form/Button";
import { queue } from "@/components/Toast";
import { MAX_SLIDES } from "@/types/scheduling.type";
import { newSlideId } from "../api/useScheduling";
import { KitMenu, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "../../components/kit/Menu";
import { KitModal } from "../../components/kit/Modal";
import { channelName } from "./liveModel";
import type { LiveStageHandle } from "./liveStage";
import type { LiveChannel, LiveFile, LiveSlides } from "./liveTypes";
import { FilePicture } from "./LiveParts";
import { SlideStrip } from "./LivePost";

/** "2nd", "3rd": the moved picture's new place. */
function ordinal(n: number): string {
  const rest = n % 100;
  const suffix = rest >= 11 && rest <= 13 ? "th" : n % 10 === 1 ? "st" : n % 10 === 2 ? "nd" : n % 10 === 3 ? "rd" : "th";
  return `${n}${suffix}`;
}

const SECTION_TITLE = "font-section text-[22px] font-extrabold uppercase leading-[1.1] tracking-[0.015em] text-co-text";

/**
 * A static post's slides, once for the post (every platform posts the same
 * order): one picture, or two or more posting as a carousel on the platforms
 * that take one. The strip is the library's Slide thumb row (SlideStrip).
 *
 * The team adds, renames, removes and drags slides; puts a picture from the
 * post on a slide, or drops files from the desktop straight onto one. A click
 * shows that slide on the posts.
 */
export default function SlidesEditor({
  slides,
  channels,
  files,
  stage,
  canEdit,
  current,
  onPick,
}: {
  slides: LiveSlides;
  channels: LiveChannel[];
  files: Map<string, LiveFile>;
  stage: LiveStageHandle;
  canEdit: boolean;
  /** The slide the posts show; a click on a slide picks it. */
  current: number;
  onPick: (position: number) => void;
}) {
  const items = slides.items;
  const carousel = items.length >= 2;
  const editable = canEdit && !!stage.setSlides;
  const [renaming, setRenaming] = useState<{ id: string; draft: string } | null>(null);
  const [choosingFor, setChoosingFor] = useState<string | null>(null);

  const entries = () => items.map((item) => ({ id: item.id, label: item.label }));
  const save = (next: { id?: string | null; label: string }[]) =>
    void Promise.resolve(stage.setSlides?.(next)).catch(() => undefined);
  const carouselNames = channels.filter((channel) => channel.kind === "CAROUSEL").map(channelName);
  const pictures = [...files.values()].filter(
    (file) => file.kind === "IMAGE" && file.status === "ready" && !file.id.startsWith("local-") && !file.id.endsWith(":cover"),
  );

  const summary = carousel
    ? carouselNames.length
      ? `${items.length} slides, posting as a carousel on ${carouselNames.join(", ")}.`
      : `${items.length} slides. None of this post's platforms takes a carousel, so each posts slide 1.`
    : "One picture. Add a slide to make it a carousel.";

  const menu = (position: number) => {
    const item = items[position];
    if (!item) return null;
    const label = item.label || `Slide ${position + 1}`;
    return (
      <KitMenuTrigger>
        <KitPressable>
          <button
            type="button"
            aria-label={`More for slide ${position + 1}, ${label}`}
            // Solid enough to read on any picture: the media glass, darker.
            className="grid size-5 place-items-center rounded-full bg-[rgba(18,18,18,0.78)] text-white shadow-[0_1px_4px_rgba(0,0,0,0.35)] outline-none backdrop-blur-[6px] hover:bg-[rgba(18,18,18,0.9)] focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            <MoreHorizontal className="size-3" strokeWidth={2.5} aria-hidden />
          </button>
        </KitPressable>
        <KitMenuPopover placement="bottom end" offset={4} className="!min-w-[180px]">
          <KitMenu
            aria-label={`More for slide ${position + 1}`}
            onAction={(key) => {
              if (key === "rename") setRenaming({ id: item.id, draft: item.label });
              else if (key === "choose") setChoosingFor(item.id);
              else if (key === "clear") void Promise.resolve(stage.placeOnSlide?.(item.id, null)).catch(() => undefined);
              else if (key === "remove") save(entries().filter((entry) => entry.id !== item.id));
            }}
          >
            <KitMenuItem id="rename">Rename</KitMenuItem>
            <KitMenuItem id="choose">{item.fileId ? "Choose another picture" : "Choose a picture"}</KitMenuItem>
            {item.fileId ? <KitMenuItem id="clear">Take the picture off</KitMenuItem> : null}
            <KitMenuItem id="remove" danger>
              Remove slide
            </KitMenuItem>
          </KitMenu>
        </KitMenuPopover>
      </KitMenuTrigger>
    );
  };

  return (
    <section aria-labelledby="live-slides" className="flex min-w-0 flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 id="live-slides" className={SECTION_TITLE}>
          Slides
        </h2>
        <p className="text-[12.5px] leading-[1.4] text-co-muted">{summary}</p>
      </header>

      {carousel ? (
        <SlideStrip
          slides={items.map((item, position) => ({
            index: position + 1,
            label: item.label || `Slide ${position + 1}`,
            fileId: item.fileId,
            key: item.id,
          }))}
          files={files}
          current={current}
          // An empty slide opens the picker; a filled one shows on the posts.
          onPick={(position) => {
            onPick(position);
            const item = items[position];
            if (editable && item && !item.fileId) setChoosingFor(item.id);
          }}
          onReorder={
            editable
              ? (from, to) => {
                  stage.reorder?.("", from, to);
                  // "Moved to 2nd" with Undo (Figma 08 carousel order, 13 Oct).
                  const moved = items[from - 1];
                  const name = (moved?.fileId ? files.get(moved.fileId)?.name : null) ?? `Picture ${from}`;
                  queue.add(
                    {
                      title: `Moved to ${ordinal(to)}`,
                      description: `${name} is now ${ordinal(to)}. Every channel that takes the carousel uses the new order.`,
                      variant: "success",
                      action: { label: "Undo", onPress: () => stage.reorder?.("", to, from) },
                    },
                    { timeout: 8000 },
                  );
                }
              : undefined
          }
          onDropFiles={editable ? (position, dropped) => stage.uploadToSlide?.(items[position].id, dropped) : undefined}
          onDropFileId={
            editable
              ? (position, fileId) => void Promise.resolve(stage.placeOnSlide?.(items[position].id, fileId)).catch(() => undefined)
              : undefined
          }
          slideMenu={editable ? menu : undefined}
          emptyLabel={editable ? "Drop or choose a picture" : undefined}
          trailing={
            editable && items.length < MAX_SLIDES ? (
              <button
                type="button"
                onClick={() => save([...entries(), { id: newSlideId(), label: "" }])}
                className="co-live-slide grid w-full place-items-center rounded-[12px] border border-dashed border-co-line2 text-co-muted outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
                style={{ aspectRatio: "4 / 5" }}
                aria-label="Add a slide"
              >
                <Plus className="size-5" aria-hidden />
              </button>
            ) : undefined
          }
        />
      ) : editable ? (
        <div>
          <Button
            size="small"
            variant="secondary"
            icon={Plus}
            iconClassName="size-3.5"
            onPress={() => save([{ id: newSlideId(), label: "" }, { id: newSlideId(), label: "" }])}
          >
            Make it a carousel
          </Button>
        </div>
      ) : null}

      {renaming ? (
        <KitModal
          isOpen
          onOpenChange={(open) => {
            if (!open) setRenaming(null);
          }}
          title="Rename the slide"
          modalClassName="max-w-[400px]"
        >
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              const { id, draft } = renaming;
              setRenaming(null);
              save(entries().map((entry) => (entry.id === id ? { ...entry, label: draft.trim() } : entry)));
            }}
          >
            <input
              autoFocus
              value={renaming.draft}
              maxLength={80}
              aria-label="Slide name"
              placeholder="What the slide is for"
              onChange={(event) => setRenaming({ ...renaming, draft: event.target.value })}
              className="co-input-own-focus co-glass-control h-11 w-full rounded-card px-3.5 text-[15px] text-co-text caret-co-accent outline-offset-0 placeholder:text-co-faint focus:outline-2 focus:outline-co-accent"
            />
            <div className="flex justify-end gap-2">
              <Button size="small" variant="ghost" onPress={() => setRenaming(null)}>
                Cancel
              </Button>
              <Button size="small" type="submit">
                Save
              </Button>
            </div>
          </form>
        </KitModal>
      ) : null}

      {choosingFor ? (
        <KitModal
          isOpen
          onOpenChange={(open) => {
            if (!open) setChoosingFor(null);
          }}
          title="Choose a picture"
          modalClassName="max-w-[520px]"
        >
          {pictures.length ? (
            <ul className="!m-0 grid list-none grid-cols-3 gap-2 !p-0 sm:grid-cols-4" aria-label="The post's pictures">
              {pictures.map((file) => (
                <li key={file.id}>
                  <button
                    type="button"
                    onClick={() => {
                      const target = choosingFor;
                      setChoosingFor(null);
                      void Promise.resolve(stage.placeOnSlide?.(target, file.id)).catch(() => undefined);
                    }}
                    className="co-live-media relative block w-full outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
                    style={{ aspectRatio: "4 / 5" }}
                    aria-label={file.name}
                  >
                    <FilePicture file={file} alt="" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] leading-relaxed text-co-text2">
              No pictures on this post yet. Drop one on the slide, or add files above.
            </p>
          )}
        </KitModal>
      ) : null}
    </section>
  );
}
